import { Box, Typography } from "@material-ui/core";
import FilterControl from "../filters/filterControl";
import Filter from "../filters/filter";
import WellStylesControl from "../controls/wellStylesControl";
import React, { useEffect } from "react";
import styled from "styled-components/macro";
import useLayerStyles from "../hooks/useLayerStyles";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Button from "@material-ui/core/Button";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import Popper from "@material-ui/core/Popper";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";
import useTheme from "@material-ui/core/styles/useTheme";

const FiltersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;
  flex: 1 1 0;
`;

const FiltersBar = ({
  filterValues,
  handleFilterValues,
  handleSelectAll,
  handleSelectNone,
  updateLayerStyles,
  graphModeVisible,
}) => {
  const { activeStyle, handleActiveStyle, styleOptions } = useLayerStyles({
    onLayerStyleChange: updateLayerStyles,
  });
  const theme = useTheme();

  useEffect(() => {
    if (graphModeVisible) {
      handleActiveStyle("default");
    }
  }, [graphModeVisible]); //eslint-disable-line

  const splitButtonOptions = [
    filterValues?.locationTypes,
    filterValues?.organizations,
    filterValues?.huc10s,
    filterValues?.sources,
    filterValues?.parameterGroups,
    filterValues?.hasFlowGraph,
  ];

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const handleMenuItemClick = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const getMoreFiltersCount = (filterValues) => {
    const keys = ["hasFlowGraph", "hasTemperatureGraph"];
    return keys.filter((key) => filterValues[key].value).length;
  };

  return (
    <>
      <FiltersSection>
        <FiltersContainer>
          <ButtonGroup
            variant="contained"
            color="primary"
            ref={anchorRef}
            aria-label="split button"
            style={{
              marginLeft: theme.spacing(1),
            }}
          >
            {selectedIndex !== 5 ? (
              <FilterControl
                width="154px"
                borderTopRightRadius={0}
                borderBottomRightRadius={0}
                appliedCount={splitButtonOptions[selectedIndex].value?.length}
                label={splitButtonOptions[selectedIndex].label}
              >
                <Filter
                  label={splitButtonOptions[selectedIndex].label}
                  name={splitButtonOptions[selectedIndex].name}
                  onChange={handleFilterValues}
                  onSelectAll={handleSelectAll}
                  onSelectNone={handleSelectNone}
                  options={splitButtonOptions[selectedIndex].options}
                  type={splitButtonOptions[selectedIndex].type}
                  value={splitButtonOptions[selectedIndex].value}
                />
              </FilterControl>
            ) : (
              <FilterControl
                width="154px"
                borderTopRightRadius={0}
                borderBottomRightRadius={0}
                appliedCount={getMoreFiltersCount(filterValues)}
                label="More Filters"
              >
                <Box display="flex" flexDirection="column">
                  <Filter
                    label="Has Flow"
                    name="hasFlowGraph"
                    onChange={handleFilterValues}
                    type="boolean"
                    value={filterValues?.hasFlowGraph?.value}
                  />
                  <Filter
                    label="Has Temperature"
                    name="hasTemperatureGraph"
                    onChange={handleFilterValues}
                    type="boolean"
                    value={filterValues?.hasTemperatureGraph?.value}
                  />
                </Box>
              </FilterControl>
            )}

            <Button
              disableElevation
              color="primary"
              variant="outlined"
              size="small"
              aria-controls={open ? "split-button-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-label="select merge strategy"
              aria-haspopup="menu"
              onClick={handleToggle}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            style={{ zIndex: 3 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id="split-button-menu">
                      {splitButtonOptions.map((option, index) => (
                        <MenuItem
                          key={option.label}
                          selected={index === selectedIndex}
                          onClick={(event) => handleMenuItemClick(event, index)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </FiltersContainer>
      </FiltersSection>

      {!graphModeVisible && (
        <FiltersSection>
          <FiltersContainer>
            <FilterControl label={`Color Data Points by ${activeStyle.name}`}>
              <Typography variant="subtitle1" gutterBottom>
                Color Data Points by
              </Typography>
              <WellStylesControl
                label="Color Data Points by"
                name="wellStyles"
                onChange={handleActiveStyle}
                options={styleOptions}
                value={activeStyle.id}
              />
            </FilterControl>
          </FiltersContainer>
        </FiltersSection>
      )}
    </>
  );
};

export default FiltersBar;
